import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

import useConfirm from "./useConfirm";
import { type ConfirmOptions } from "../context/ConfirmContextProvider";
import _ from "lodash";

type PromprtProps = Partial<ConfirmOptions> & {
    isDirty?: boolean;
    isNavigateOnCancel?: boolean;
    disableNavigateOnConfirm?: boolean;
    isInterpretationDirty?: boolean;
};

const blockOnInterpretation = (
    currentPathname: string,
    nextPathname: string
): boolean => {
    return !(
        currentPathname.endsWith("interpretation") &&
        nextPathname.includes("interpretation")
    );
};

const usePrompt = ({
    title,
    description,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    isDirty = false,
    isNavigateOnCancel = true,
    disableNavigateOnConfirm = false,
    isInterpretationDirty = false,
}: PromprtProps) => {
    const [nextPathname, setNextPathname] = useState("");
    const isConfirming = useRef(false);

    const blocker = useBlocker((tx) => {
        const currentRoute = tx.currentLocation;
        const nextRoute = tx.nextLocation;
        setNextPathname(tx.nextLocation.pathname);
        return (
            (isDirty &&
                blockOnInterpretation(
                    currentRoute.pathname,
                    nextRoute.pathname
                )) ||
            (isInterpretationDirty &&
                !nextRoute.pathname.includes("interpretation"))
        );
    });

    const { show } = useConfirm();

    const confirm = useCallback(async () => {
        if (
            isDirty ||
            (isInterpretationDirty && !nextPathname.includes("interpretation"))
        ) {
            return await new Promise<boolean>((resolve) => {
                // filter out the options that are undefined
                const filteredOptions = _.pickBy(
                    {
                        title,
                        description,
                        confirmText,
                        cancelText,
                    },
                    (v) => v !== undefined
                );
                show({
                    ...filteredOptions,
                    onConfirm: async () => {
                        await onConfirm?.();
                        resolve(!disableNavigateOnConfirm);
                    },
                    onCancel: async () => {
                        await onCancel?.();
                        resolve(isNavigateOnCancel);
                    },
                    onClose: () => {
                        resolve(false);
                    },
                });
            });
        } else return Promise.resolve(true);
    }, [
        cancelText,
        confirmText,
        isDirty,
        onCancel,
        onConfirm,
        show,
        title,
        nextPathname,
    ]);

    useEffect(() => {
        if (blocker.state === "blocked" && !isConfirming.current) {
            isConfirming.current = true;
            confirm()
                .then((result) => {
                    isConfirming.current = false;
                    if (result) {
                        blocker.proceed();
                    } else {
                        blocker.reset();
                    }
                })
                .catch(() => {
                    isConfirming.current = false;
                    blocker.reset();
                });
        }
    }, [blocker.state, confirm]);

    return { confirm, nextPathname };
};

export default usePrompt;
